import type {GridColumnVisibilityModel} from "@mui/x-data-grid-pro";
import type {TableColumnStorageConfig} from "../types";
import {visibilityModelsEqual} from "./visibilityModelsEqual";

export type ExportMode = 'visible' | 'all';

/** Column order, visibility and widths of a table, as shown and as persisted. */
export interface ColumnLayout {
    order: string[];
    visibility: Record<string, boolean>;
    widths: Record<string, number>;
}

/** What the CSV export needs from the table: its full column order and visibility. */
export interface ExportColumnLayout {
    order: string[];
    visibility: GridColumnVisibilityModel;
}

// Reconcile a persisted column order with the current default columns: drop fields
// no longer present in the defaults and insert any new default field at its default
// position (right after its left neighbor). Keeps newly added columns — e.g. the
// brand-specific Semiconductor Tool/Chamber — visible for users who already have a
// saved config, without forcing a manual reset.
export const reconcileColumnOrder = (savedOrder: string[] | undefined, defaultFields: string[]): string[] => {
    if (!savedOrder?.length) return defaultFields;

    const result = savedOrder.filter((f) => defaultFields.includes(f));
    defaultFields.forEach((field, idx) => {
        if (result.includes(field)) return;
        const prev = defaultFields[idx - 1];
        const insertAt = prev && result.includes(prev) ? result.indexOf(prev) + 1 : idx;
        result.splice(insertAt, 0, field);
    });
    return result;
};

/** Moves the fixed fields (e.g. the row actions) to the end, in the given order. */
export const pinFieldsLast = (order: string[], fixedFields: string[] = []): string[] => {
    if (!fixedFields.length) return order;
    return [
        ...order.filter((f) => !fixedFields.includes(f)),
        ...fixedFields.filter((f) => order.includes(f)),
    ];
};

/** The column definitions in the given order; stale and missing fields are reconciled. */
export const orderColumns = <C extends { field: string }>(defs: C[], order: string[]): C[] => {
    const byField = new Map(defs.map((def) => [def.field, def]));
    return reconcileColumnOrder(order, defs.map((def) => def.field)).map((field) => byField.get(field) as C);
};

/**
 * Moves `activeField` to the position of `overField`, like dragging a row onto another
 * in a sortable list. Fixed fields never move and stay at the end.
 */
export const moveField = (
    order: string[],
    activeField: string,
    overField: string,
    fixedFields: string[] = []
): string[] => {
    const movable = order.filter((f) => !fixedFields.includes(f));
    const from = movable.indexOf(activeField);
    const to = movable.indexOf(overField);
    if (from === -1 || to === -1 || from === to) return order;

    const next = [...movable];
    next.splice(from, 1);
    next.splice(to, 0, activeField);
    return [...next, ...order.filter((f) => fixedFields.includes(f))];
};

// A missing key in a visibility model means "visible" (MUI's "Show all" deletes keys).
// Storing an explicit value per field keeps that apart from "not configured yet".
export const normalizeVisibility = (
    model: GridColumnVisibilityModel,
    fields: string[]
): Record<string, boolean> =>
    Object.fromEntries(fields.map((field) => [field, model[field] !== false]));

const sameOrder = (a: string[], b: string[]) =>
    a.length === b.length && a.every((field, i) => field === b[i]);

export const columnLayoutsEqual = (a: ColumnLayout, b: ColumnLayout): boolean =>
    sameOrder(a.order, b.order) &&
    visibilityModelsEqual(a.visibility, b.visibility) &&
    JSON.stringify(a.widths) === JSON.stringify(b.widths);

interface ResolveOptions {
    saved: TableColumnStorageConfig | null;
    defaultFields: string[];
    /** Missing fields are visible. */
    defaultVisibility?: GridColumnVisibilityModel;
    /** Shown regardless of the saved config, but never persisted as visible. */
    forceVisible?: string[];
    fixedFields?: string[];
}

export interface ResolvedColumnLayout {
    /** What the table shows. */
    layout: ColumnLayout;
    /** What is persisted: the layout without the forced columns. */
    persistedVisibility: Record<string, boolean>;
    /** The reconciled order differs from the saved one. */
    orderChanged: boolean;
}

/** The layout of a table from its saved config (if any) and its current default columns. */
export const resolveColumnLayout = ({
    saved,
    defaultFields,
    defaultVisibility = {},
    forceVisible = [],
    fixedFields = [],
}: ResolveOptions): ResolvedColumnLayout => {
    const order = pinFieldsLast(reconcileColumnOrder(saved?.columnOrder, defaultFields), fixedFields);
    const persistedVisibility = normalizeVisibility(
        {...defaultVisibility, ...saved?.columnVisibilityModel},
        defaultFields
    );
    const forced = Object.fromEntries(
        forceVisible.filter((f) => defaultFields.includes(f)).map((f) => [f, true])
    );
    const widths = Object.fromEntries(
        Object.entries(saved?.columnWidths ?? {}).filter(([field]) => defaultFields.includes(field))
    );

    return {
        layout: {order, visibility: {...persistedVisibility, ...forced}, widths},
        persistedVisibility,
        orderChanged: !!saved?.columnOrder?.length && !sameOrder(order, saved.columnOrder),
    };
};

/**
 * The visibility to persist after a change in the table. Forced columns keep their
 * persisted value — unless the user hid them, which is stored like any other choice.
 */
export const visibilityToPersist = (
    next: Record<string, boolean>,
    persisted: Record<string, boolean>,
    forceVisible: string[] = []
): Record<string, boolean> =>
    Object.fromEntries(
        Object.entries(next).map(([field, visible]) => [
            field,
            forceVisible.includes(field) && visible ? persisted[field] ?? true : visible,
        ])
    );

/**
 * The CSV columns in table order. Fields the table doesn't know (CSV-only) keep their
 * position relative to their default neighbors; fields the CSV doesn't know (row
 * actions) are dropped. Without a layout the CSV's default order applies.
 */
export const orderExportColumns = <D extends { field: string }>(
    defs: D[],
    layout: ExportColumnLayout | null,
    mode: ExportMode
): D[] => {
    if (!layout) return defs;

    const ordered = orderColumns(defs, layout.order);
    if (mode === 'all') return ordered;

    return ordered.filter((def) => layout.order.includes(def.field) && layout.visibility[def.field] !== false);
};
